/**
 * Task Filters Component
 *
 * Filter and sort controls for task lists.
 */

import type { TaskFilters, TaskGroupBy } from '@/types';

interface TaskFiltersProps {
  filters: TaskFilters;
  onFiltersChange: (filters: TaskFilters) => void;
  groupBy?: TaskGroupBy;
  onGroupByChange?: (groupBy: TaskGroupBy | undefined) => void;
  showSearch?: boolean;
  showGroupBy?: boolean;
  showCompleted?: boolean;
  onShowCompletedChange?: (show: boolean) => void;
}

export function TaskFiltersBar({
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
  showSearch = true,
  showGroupBy = true,
  showCompleted = false,
  onShowCompletedChange,
}: TaskFiltersProps) {
  const updateFilter = (key: keyof TaskFilters, value: string | undefined) => {
    onFiltersChange({
      ...filters,
      [key]: value || undefined,
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-3 p-4 bg-gray-50 rounded-lg">
      {/* Search */}
      {showSearch && (
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder="Search tasks..."
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      )}

      {/* Status filter */}
      <select
        value={filters.status || ''}
        onChange={(e) => updateFilter('status', e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">All Status</option>
        <option value="pending">Pending</option>
        <option value="in_progress">In Progress</option>
        <option value="completed">Completed</option>
      </select>

      {/* Priority filter */}
      <select
        value={filters.priority || ''}
        onChange={(e) => updateFilter('priority', e.target.value)}
        className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">All Priority</option>
        <option value="high">High</option>
        <option value="medium">Medium</option>
        <option value="low">Low</option>
      </select>

      {/* Group by */}
      {showGroupBy && onGroupByChange && (
        <select
          value={groupBy || ''}
          onChange={(e) =>
            onGroupByChange(e.target.value as TaskGroupBy | undefined || undefined)
          }
          className="px-3 py-2 border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">No Grouping</option>
          <option value="status">Group by Status</option>
          <option value="priority">Group by Priority</option>
          <option value="dueDate">Group by Due Date</option>
          <option value="topic">Group by Topic</option>
        </select>
      )}

      {/* Show completed toggle */}
      {onShowCompletedChange && (
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showCompleted}
            onChange={(e) => onShowCompletedChange(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="text-sm text-gray-600">Show Completed</span>
        </label>
      )}

      {/* Clear filters */}
      {(filters.status || filters.priority || filters.search) && (
        <button
          onClick={() => onFiltersChange({})}
          className="px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
        >
          Clear filters
        </button>
      )}
    </div>
  );
}
